import { TUNABLES } from "../config";
import { advanceTick, applyCommand, hashString, type RunState, withHeld } from "../core";
import type { TurnEntry } from "./protocol";
import { sha256Hex } from "./sha256";

/**
 * A client's half of the relay (B3.4, D-023, D-024): turn n is applied at tick n, through the
 * door, with the seat the room stamped on each command; then the tick advances. Every client runs
 * this on the same turns from the same seed, so they hold the same state, and a hash says so.
 * Pure: the browser client (B3.6) and the tests both drive it.
 */
export interface TickHash {
  readonly tick: number;
  /** The page's vote: `voteOf` the state at the tick. */
  readonly hash: string;
}

/** Whether a page reports its state's hash at this tick (every `hashEveryTicks`, D-027). */
export function hashDue(tick: number, every: number = TUNABLES.net.hashEveryTicks): boolean {
  return tick % every === 0;
}

/**
 * One entry of a turn, applied as every page applies it: a command through the door as the seat
 * the room stamped (K0), or who holds the seats from here on (B3.7: a seat left to its script is
 * played by it from the next script turn, one taken back stands it down).
 */
export function applyEntry(state: RunState, entry: TurnEntry): RunState {
  return "held" in entry
    ? withHeld(state, entry.held)
    : applyCommand(state, entry.seat, entry.command);
}

/** FNV-1a over the state's JSON: the byte guard of the replays, the golden runs and the hook. */
export function stateHash(state: RunState): number {
  return hashString(JSON.stringify(state));
}

/** What a client reports (K7.1): SHA-256 over the state's JSON, which no forged state shares. */
export function voteOf(state: RunState): string {
  return sha256Hex(JSON.stringify(state));
}

/**
 * Applies the turns that begin at `from` in order. A turn the state already passed is skipped (a
 * replay that overlaps); a gap throws, because a client that skipped a turn holds another game.
 * Returns the state and the hashes due on the ticks it reached.
 */
export function applyTurns(
  state: RunState,
  from: number,
  turns: readonly (readonly TurnEntry[])[],
  hashEvery: number = TUNABLES.net.hashEveryTicks,
): { readonly state: RunState; readonly hashes: readonly TickHash[] } {
  let next = state;
  const hashes: TickHash[] = [];
  turns.forEach((turn, index) => {
    const at = from + index;
    if (at < next.tick || next.status !== "running") {
      return;
    }
    if (at > next.tick) {
      throw new RangeError(`turn ${at} reached a client at tick ${next.tick}: a turn is missing`);
    }
    for (const entry of turn) {
      next = applyEntry(next, entry);
    }
    next = advanceTick(next);
    if (hashDue(next.tick, hashEvery)) {
      hashes.push({ tick: next.tick, hash: voteOf(next) });
    }
  });
  return { state: next, hashes };
}
